import type { ReactElement } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'

import ModalContext from 'contexts/ModalContext'
import noop from 'lib/function/noop'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

export function useMobileCartPanelState(
  createCartModal: (onClose: () => void) => ReactElement,
  closeSignal?: number,
  onCartOpenChange: (isOpen: boolean) => void = noop,
) {
  const showModal = useContext(ModalContext)
  const closeCartRef = useRef<(() => void) | undefined>(undefined)
  const isCartOpenRef = useRef(false)
  const previousCloseSignalRef = useRef(closeSignal)

  const closeCart = useCallback(() => {
    closeCartRef.current?.()
  }, [])

  const openCart = useCallback(() => {
    if (isCartOpenRef.current) {
      return
    }

    isCartOpenRef.current = true
    onCartOpenChange(true)
    const modalPromise = showModal<void>((resolve) => {
      closeCartRef.current = () => resolve()
      return createCartModal(closeCartRef.current)
    })

    modalPromise.finally(() => {
      isCartOpenRef.current = false
      closeCartRef.current = undefined
      onCartOpenChange(false)
    })
  }, [createCartModal, onCartOpenChange, showModal])

  const handleCartCommand = useCallback(() => {
    if (isCartOpenRef.current) {
      closeCart()
      return
    }

    openCart()
  }, [closeCart, openCart])

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignalRef.current) return

    previousCloseSignalRef.current = closeSignal
    closeCart()
  }, [closeCart, closeSignal])

  useEventSubscription('open_cart_panel', handleCartCommand)
  useEventSubscription('reset_chat_state', closeCart)
}
